import React, { useRef, useEffect, useCallback } from 'react';
import { Bold, Italic, Underline, List, ListOrdered, AlignLeft, AlignCenter, AlignRight, Type, Heading1, Heading2 } from 'lucide-react';

const RichTextEditor = ({ value, onChange, placeholder, className = '' }) => {
  const editorRef = useRef(null);
  const isInternalChange = useRef(false);

  // Initialize content
  useEffect(() => {
    if (editorRef.current && !isInternalChange.current) {
      if (editorRef.current.innerHTML !== value) {
        editorRef.current.innerHTML = value || '';
      }
    }
    isInternalChange.current = false;
  }, [value]);

  const handleInput = useCallback(() => {
    if (editorRef.current) {
      isInternalChange.current = true;
      onChange(editorRef.current.innerHTML);
    }
  }, [onChange]);

  const execCommand = (command, value = null) => {
    editorRef.current?.focus();
    document.execCommand(command, false, value);
    handleInput();
  };

  const handleFormat = (format) => {
    switch (format) {
      case 'bold':
        execCommand('bold');
        break;
      case 'italic':
        execCommand('italic');
        break;
      case 'underline':
        execCommand('underline');
        break;
      case 'ul':
        execCommand('insertUnorderedList');
        break;
      case 'ol':
        execCommand('insertOrderedList');
        break;
      case 'left':
        execCommand('justifyLeft');
        break;
      case 'center':
        execCommand('justifyCenter');
        break;
      case 'right':
        execCommand('justifyRight');
        break;
      case 'h1':
        execCommand('formatBlock', '<h1>');
        break;
      case 'h2':
        execCommand('formatBlock', '<h2>');
        break;
      case 'p':
        execCommand('formatBlock', '<p>');
        break;
      default:
        break;
    }
  };

  const handleFontSize = (size) => {
    execCommand('fontSize', size);
  };

  const ToolbarButton = ({ icon: Icon, onClick, title, active = false }) => (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className={`p-1.5 rounded hover:bg-slate-600 transition-colors ${
        active ? 'bg-slate-600 text-amber-400' : 'text-slate-300'
      }`}
    >
      <Icon className="w-4 h-4" />
    </button>
  );

  const ToolbarDivider = () => (
    <div className="w-px h-6 bg-slate-600 mx-1" />
  );

  return (
    <div className={`border border-slate-700 rounded-lg overflow-hidden ${className}`}>
      {/* Toolbar */}
      <div className="bg-slate-800 border-b border-slate-700 p-2 flex flex-wrap items-center gap-0.5">
        {/* Text Format */}
        <ToolbarButton icon={Bold} onClick={() => handleFormat('bold')} title="Bold (Ctrl+B)" />
        <ToolbarButton icon={Italic} onClick={() => handleFormat('italic')} title="Italic (Ctrl+I)" />
        <ToolbarButton icon={Underline} onClick={() => handleFormat('underline')} title="Underline (Ctrl+U)" />
        
        <ToolbarDivider />
        
        {/* Headings */}
        <ToolbarButton icon={Heading1} onClick={() => handleFormat('h1')} title="Heading 1" />
        <ToolbarButton icon={Heading2} onClick={() => handleFormat('h2')} title="Heading 2" />
        <ToolbarButton icon={Type} onClick={() => handleFormat('p')} title="Normal Text" />
        
        <ToolbarDivider />
        
        {/* Font Size */}
        <select
          onChange={(e) => handleFontSize(e.target.value)}
          className="bg-slate-700 text-slate-300 text-xs rounded px-2 py-1 border border-slate-600 focus:outline-none focus:border-amber-500"
          title="Font Size"
          defaultValue="3"
        >
          <option value="1">Small</option>
          <option value="2">Normal</option>
          <option value="3">Medium</option>
          <option value="4">Large</option>
          <option value="5">X-Large</option>
          <option value="6">XX-Large</option>
        </select>
        
        <ToolbarDivider />
        
        {/* Lists */}
        <ToolbarButton icon={List} onClick={() => handleFormat('ul')} title="Bullet List" />
        <ToolbarButton icon={ListOrdered} onClick={() => handleFormat('ol')} title="Numbered List" />
        
        <ToolbarDivider />
        
        {/* Alignment */}
        <ToolbarButton icon={AlignLeft} onClick={() => handleFormat('left')} title="Align Left" />
        <ToolbarButton icon={AlignCenter} onClick={() => handleFormat('center')} title="Align Center" />
        <ToolbarButton icon={AlignRight} onClick={() => handleFormat('right')} title="Align Right" />
      </div>
      
      {/* Editor */}
      <div
        ref={editorRef}
        contentEditable
        onInput={handleInput}
        data-placeholder={placeholder}
        className="min-h-[150px] max-h-[300px] overflow-y-auto p-4 bg-slate-900 text-white text-sm focus:outline-none
          [&:empty]:before:content-[attr(data-placeholder)] [&:empty]:before:text-slate-500
          [&_h1]:text-xl [&_h1]:font-bold [&_h1]:mb-2 [&_h1]:text-white
          [&_h2]:text-lg [&_h2]:font-semibold [&_h2]:mb-2 [&_h2]:text-white
          [&_p]:mb-2 [&_p]:text-slate-300
          [&_ul]:list-disc [&_ul]:ml-4 [&_ul]:mb-2
          [&_ol]:list-decimal [&_ol]:ml-4 [&_ol]:mb-2
          [&_li]:text-slate-300 [&_li]:mb-1
          [&_b]:font-bold [&_strong]:font-bold
          [&_i]:italic [&_em]:italic
          [&_u]:underline"
      />
    </div>
  );
};

export default RichTextEditor;
